import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  IconButton,
  Button,
  Avatar,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PeopleIcon from "@material-ui/icons/People";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import GroupIcon from "@material-ui/icons/Group";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  backButton: { marginRight: theme.spacing(1) },
  table: { minWidth: 400 },
  avatar: { width: 40, height: 40 },
  nameCell: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
}));

const GroupsList = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const connectionId = params.get("connectionId");

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaveModal, setLeaveModal] = useState({ open: false, group: null });

  useEffect(() => {
    if (!connectionId) {
      history.replace("/group-management");
      return;
    }
    const fetchGroups = async () => {
      try {
        const { data } = await api.get(`/group-management/connections/${connectionId}/groups`);
        setGroups(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [connectionId, history]);

  const handleBack = () => history.push("/group-management");
  const handleParticipants = (group) => {
    history.push(`/group-management/groups/${encodeURIComponent(group.id)}/participants?connectionId=${connectionId}`);
  };
  const handleLeave = async () => {
    if (!leaveModal.group) return;
    try {
      await api.delete(`/group-management/groups/${encodeURIComponent(leaveModal.group.id)}`, {
        params: { connectionId },
      });
      toast.success(i18n.t("groupManagement.leftGroup"));
      setGroups((prev) => prev.filter((g) => g.id !== leaveModal.group.id));
      setLeaveModal({ open: false, group: null });
    } catch (err) {
      toastError(err);
    }
  };

  if (!connectionId) return null;

  return (
    <MainContainer>
      <MainHeader>
        <Button className={classes.backButton} onClick={handleBack} startIcon={<ArrowBackIcon />}>
          {i18n.t("groupManagement.back")}
        </Button>
        <Title>{i18n.t("groupManagement.listGroups")}</Title>
      </MainHeader>

      <Box className={classes.root}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" style={{ width: 56 }} />
                  <TableCell>{i18n.t("groupManagement.groupName")}</TableCell>
                  <TableCell align="right">{i18n.t("groupManagement.participantsCount")}</TableCell>
                  <TableCell align="center">{i18n.t("groupManagement.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="textSecondary">{i18n.t("groupManagement.noGroups")}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  groups.map((group) => (
                    <TableRow key={group.id}>
                      <TableCell padding="checkbox">
                        <Avatar
                          src={group.pictureUrl}
                          className={classes.avatar}
                          variant="circular"
                        >
                          <GroupIcon />
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Box className={classes.nameCell}>
                          <Typography variant="body2">{group.subject || group.id}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">{group.participantsCount ?? "-"}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleParticipants(group)} title={i18n.t("groupManagement.seeParticipants")}>
                          <PeopleIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => setLeaveModal({ open: true, group })} title={i18n.t("groupManagement.leaveGroup")}>
                          <ExitToAppIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ConfirmationModal
        open={leaveModal.open}
        onClose={() => setLeaveModal({ open: false, group: null })}
        onConfirm={handleLeave}
        title={i18n.t("groupManagement.leaveGroup")}
      >
        {leaveModal.group && (
          <Box>
            <Typography>
              {i18n.t("groupManagement.leaveGroupConfirm", { name: leaveModal.group.subject || leaveModal.group.id })}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {i18n.t("groupManagement.leaveGroupOthersRemain")}
            </Typography>
          </Box>
        )}
      </ConfirmationModal>
    </MainContainer>
  );
};

export default GroupsList;
